import { formatJobNumber } from '@fieldops/shared'
import { useQueries, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { cn } from '../../lib/cn.js'
import { feedStamp, money } from '../../lib/format.js'
import { useSession } from '../../lib/session.js'
import { useTRPC } from '../../lib/trpc.js'
import { FileViewer, viewerMedia, type ViewerFile } from '../FileViewer.js'
import { Icon, type IconName } from '../Icon.js'
import { buildFeed, emailFeedEntries, type FeedEntry, type FeedFile } from '../job-card/feed.js'
import type { Job } from '../job-card/types.js'
import { humanise } from '../ui.js'
import { QuickActions, type Capture } from './FieldCapture.js'
import { FieldLayer } from './kit.js'

export type FeedFilter = 'all' | 'media' | 'notes' | 'activity'

const FILTERS: { value: FeedFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'media', label: 'Photos & video' },
  { value: 'notes', label: 'Notes' },
  { value: 'activity', label: 'Activity' },
]

type Group = Exclude<FeedFilter, 'all'>

/** One line of the phone's feed: a feed entry, or a payment against the bill. */
interface Row {
  id: string
  group: Group
  icon: IconName
  /** The icon tile's colours: [background, glyph]. */
  tint: 'brand' | 'mail' | 'money' | 'quiet'
  who: string | null
  at: Date
  text: string
  body?: string
  meta?: string
  files: RowFile[]
}

interface RowFile extends FeedFile {
  media: 'image' | 'video' | 'audio' | 'document'
}

/** The feed sits on the ink-100 page, so even the quiet tile is drawn in white. */
const TINT: Record<Row['tint'], string> = {
  brand: 'bg-brand-100 text-brand-700',
  mail: 'bg-white text-ink-900 ring-1 ring-ink-400 ring-inset',
  money: 'bg-ink-900 text-brand-200',
  quiet: 'bg-white text-ink-500 ring-1 ring-ink-200 ring-inset',
}

/**
 * Everything that happened on the job, newest first, as one timeline.
 *
 * The office's activity column read the same rows (`buildFeed` and the job's
 * mail), so the phone and the desk cannot tell two stories. The chips are the
 * mockup's: what was captured, what was written, and everything else. A
 * voice clip is a note, because it is somebody saying what they found.
 */
export function FieldFeed({
  job,
  filter,
  onFilter,
  onClose,
  capture,
  canCapture,
  onNote,
  onBilling,
}: {
  job: Job
  filter: FeedFilter
  onFilter: (filter: FeedFilter) => void
  onClose: () => void
  capture: Capture
  canCapture: boolean
  onNote: () => void
  onBilling?: () => void
}) {
  const trpc = useTRPC()
  const { can } = useSession()
  const [openId, setOpenId] = useState<string | null>(null)

  const mail = useQuery(trpc.inbox.forJob.queryOptions({ jobId: job.id }))
  const invoices = useQueries({
    queries: can('INVOICE_VIEW')
      ? job.invoices.map((invoice) => trpc.invoices.byId.queryOptions({ id: invoice.id }))
      : [],
  })
  const payments = invoices.flatMap((query) => query.data?.payments ?? [])

  const rows: Row[] = [
    ...[...buildFeed(job), ...emailFeedEntries(mail.data ?? [])].map(rowOf),
    ...payments.map((payment) => ({
      id: `payment:${payment.id}`,
      group: 'activity' as const,
      icon: 'pricing' as const,
      tint: 'money' as const,
      who: null,
      at: new Date(payment.receivedAt),
      text: paymentSentence(payment),
      files: [],
    })),
  ].sort((a, b) => b.at.getTime() - a.at.getTime())

  const shown = filter === 'all' ? rows : rows.filter((row) => row.group === filter)
  // Clips play in the row, so the viewer steps through what has to be opened.
  const viewable: ViewerFile[] = shown.flatMap((row) =>
    row.files.filter((file) => file.media === 'image' || file.media === 'document'),
  )

  return (
    <FieldLayer
      title="Job feed"
      subtitle={`${job.client.name} · ${formatJobNumber(job)}`}
      onClose={onClose}
      footer={
        <div className="min-w-0 flex-1">
          <QuickActions
            capture={capture}
            canCapture={canCapture}
            onNote={onNote}
            onBilling={onBilling}
          />
        </div>
      }
    >
      <div
        role="tablist"
        aria-label="Show"
        className="flex gap-1.5 overflow-x-auto bg-white px-4 pt-1 pb-3 shadow-[inset_0_-1px_0_var(--color-ink-200)] [scrollbar-width:none]"
      >
        {FILTERS.map((option) => {
          const active = option.value === filter
          const count =
            option.value === 'all'
              ? rows.length
              : rows.filter((row) => row.group === option.value).length
          return (
            <button
              key={option.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onFilter(option.value)}
              className={cn(
                'h-8 shrink-0 rounded-md px-3 text-[14px] font-semibold whitespace-nowrap ring-1 ring-inset',
                active ? 'bg-ink-900 text-white ring-ink-900' : 'bg-white text-ink-700 ring-ink-300',
              )}
            >
              {option.label} <span className="font-normal opacity-70">{count}</span>
            </button>
          )
        })}
      </div>

      <ol className="mx-auto flex w-full max-w-[640px] flex-col px-4 pt-4 pb-6">
        {shown.map((row) => (
          <li key={row.id} className="flex gap-3">
            <div className="flex w-8 shrink-0 flex-col items-center">
              <span className={cn('grid size-8 shrink-0 place-items-center rounded-md', TINT[row.tint])}>
                <Icon name={row.icon} className="size-4" strokeWidth={1.9} />
              </span>
              <span aria-hidden="true" className="my-1 min-h-3.5 w-0.5 flex-1 bg-ink-200" />
            </div>
            <div className="min-w-0 flex-1 pt-1 pb-[18px]">
              <div className="flex items-baseline gap-2">
                <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-ink-700">
                  {row.who ?? 'FieldOps'}
                </span>
                <span className="shrink-0 text-[12px] text-ink-400">{feedStamp(row.at)}</span>
              </div>
              <p className="mt-0.5 text-[15px] leading-snug text-pretty text-ink-900">{row.text}</p>
              {row.body ? (
                <p className="mt-1 text-[15px] leading-relaxed whitespace-pre-wrap text-ink-800">
                  {row.body}
                </p>
              ) : null}
              {row.meta ? <p className="mt-0.5 text-[13px] text-ink-500">{row.meta}</p> : null}
              {row.files.length > 0 ? <RowFiles files={row.files} onOpen={setOpenId} /> : null}
            </div>
          </li>
        ))}
        {shown.length === 0 ? (
          <li className="px-5 py-10 text-center text-[15px] text-ink-500">Nothing here yet.</li>
        ) : null}
      </ol>

      <FileViewer files={viewable} openId={openId} onClose={() => setOpenId(null)} />
    </FieldLayer>
  )
}

function RowFiles({ files, onOpen }: { files: RowFile[]; onOpen: (id: string) => void }) {
  const images = files.filter((file) => file.media === 'image')
  const rest = files.filter((file) => file.media !== 'image')

  return (
    <div className="mt-2 flex flex-col gap-2">
      {images.length > 0 ? (
        <div className="grid max-w-[320px] grid-cols-3 gap-1.5">
          {images.map((file) =>
            file.url ? (
              <button
                key={file.id}
                type="button"
                onClick={() => onOpen(file.id)}
                aria-label={`Open ${file.name}`}
                className="aspect-square overflow-hidden rounded-md bg-ink-200"
              >
                <img src={file.url} alt="" loading="lazy" decoding="async" className="size-full object-cover" />
              </button>
            ) : (
              <span key={file.id} className="aspect-square rounded-md bg-ink-200" />
            ),
          )}
        </div>
      ) : null}
      {rest.map((file) =>
        file.media === 'video' && file.url ? (
          <video
            key={file.id}
            src={file.url}
            controls
            playsInline
            preload="metadata"
            className="aspect-[4/3] w-full max-w-[280px] rounded-md bg-ink-900"
          />
        ) : file.media === 'audio' && file.url ? (
          <audio key={file.id} src={file.url} controls preload="none" className="w-full max-w-[320px]" />
        ) : (
          <button
            key={file.id}
            type="button"
            disabled={!file.url}
            onClick={() => onOpen(file.id)}
            className="flex max-w-[320px] items-center gap-2.5 rounded-md bg-white px-3 py-2 text-left ring-1 ring-ink-200 ring-inset"
          >
            <span className="grid size-8 shrink-0 place-items-center rounded-sm bg-ink-100 text-[10px] font-bold text-ink-600">
              {file.kind}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[14px] font-medium text-ink-900">{file.name}</span>
              <span className="block truncate text-[12px] text-ink-500">{file.meta}</span>
            </span>
          </button>
        ),
      )}
    </div>
  )
}

function mediaOf(file: FeedFile): RowFile['media'] {
  const media = viewerMedia(file)
  return media === 'pdf' || media === 'other' ? 'document' : media
}

function rowOf(entry: FeedEntry): Row {
  const files = (entry.files ?? []).map((file) => ({ ...file, media: mediaOf(file) }))
  const base = { id: entry.id, who: entry.who, at: entry.at, files, ...(entry.body ? { body: entry.body } : {}), ...(entry.meta ? { meta: entry.meta } : {}) }

  if (entry.kind === 'note') return { ...base, group: 'notes', icon: 'note', tint: 'brand', text: entry.text }

  if (files.length > 0 && (entry.kind === 'photo' || entry.kind === 'file')) {
    if (files.every((file) => file.media === 'audio')) {
      return {
        ...base,
        group: 'notes',
        icon: 'mic',
        tint: 'brand',
        text: files.length === 1 ? 'Voice note' : `${files.length} voice notes`,
      }
    }
    if (files.every((file) => file.media === 'image' || file.media === 'video')) {
      const videos = files.filter((file) => file.media === 'video').length
      return {
        ...base,
        group: 'media',
        icon: videos > 0 ? 'video' : 'camera',
        tint: 'brand',
        text: mediaSentence(files.length - videos, videos),
      }
    }
  }

  if (entry.kind === 'email') return { ...base, group: 'activity', icon: 'mail', tint: 'mail', text: entry.subject ? `${entry.text}: ${entry.subject}` : entry.text }
  if (entry.kind === 'quote' || entry.kind === 'invoice') {
    return {
      ...base,
      group: 'activity',
      icon: 'invoices',
      tint: 'money',
      text: entry.amount != null ? `${entry.text} · ${money(entry.amount)}` : entry.text,
    }
  }
  if (entry.kind === 'booking') return { ...base, group: 'activity', icon: 'schedule', tint: 'quiet', text: `Booked ${entry.text}` }
  return { ...base, group: 'activity', icon: entry.kind === 'file' ? 'quotes' : 'today', tint: 'quiet', text: entry.text }
}

function mediaSentence(photos: number, videos: number): string {
  const parts = [
    photos > 0 ? (photos === 1 ? '1 photo' : `${photos} photos`) : null,
    videos > 0 ? (videos === 1 ? '1 video' : `${videos} videos`) : null,
  ].filter(Boolean)
  return `${parts.join(' and ')} from site`
}

function paymentSentence(payment: { amount: number; method: string; reference: string | null }): string {
  if (payment.amount < 0) return `Reversed ${money(-payment.amount)} ${humanise(payment.method).toLowerCase()}`
  const amount = money(payment.amount)
  const ref = payment.reference ? `, ref ${payment.reference}` : ''
  if (payment.method === 'CASH') return `Recorded ${amount} cash${ref}`
  if (payment.method === 'CARD') return `Took ${amount} by card${ref}`
  if (payment.method === 'BANK_TRANSFER') return `Recorded a ${amount} bank transfer${ref}`
  return `Recorded ${amount}, ${humanise(payment.method).toLowerCase()}${ref}`
}

